/**
 * Confirmation dialog for deleting a chat session.
 *
 * Manages its own open state via AlertDialogTrigger. The parent passes
 * a trigger element as `children` and an `onDeleted` callback.
 *
 * @module components/chat/delete-chat-dialog
 */
import type { ReactNode } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { useChatEvents } from "@/hooks/use-chat-events";
import { chatService } from "@/services/chat-service";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DeleteChatDialogProps {
  /** Chat ID to delete */
  chatId: string;
  /** Called after a successful delete so the parent can navigate */
  onDeleted: () => void;
  /**
   * Optional callback fired when the in-flight delete state changes.
   * Lets parents swap their trigger icon for a spinner while the request
   * is in flight (e.g. the sidebar trash → loader transition).
   */
  onDeletingChange?: (isDeleting: boolean) => void;
  /** Trigger element that opens the dialog */
  children: ReactNode;
}

/**
 * Renders a trigger + confirmation alert dialog for deleting a chat.
 */
export function DeleteChatDialog({
  chatId,
  onDeleted,
  onDeletingChange,
  children,
}: DeleteChatDialogProps) {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const chatEvents = useChatEvents();

  const setDeleting = (next: boolean) => {
    setIsDeleting(next);
    onDeletingChange?.(next);
  };

  const handleDelete = async () => {
    if (!chatId) return;
    try {
      setDeleting(true);
      const result = await chatService.deleteChat(chatId);
      if (result.success) {
        chatEvents.emit("delete-chat", chatId);
        setOpen(false);
        toast.success("Chat deleted");
        onDeleted();
      } else {
        toast.error("Failed to delete chat");
      }
    } catch {
      toast.error("Failed to delete chat");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Chat</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this chat? This action cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
